import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface SprintPhase {
  name: string;
  nameEn: string;
  icon: string;
  duration: string;
  activities: string[];
  color: string;
  angle: number;
}

const PHASES: SprintPhase[] = [
  {
    name: '产品待办',
    nameEn: 'Backlog',
    icon: '📋',
    duration: '持续维护',
    activities: ['需求收集', '优先级排序', '用户故事编写'],
    color: '#FF6B35',
    angle: -60,
  },
  {
    name: '冲刺规划',
    nameEn: 'Sprint Planning',
    icon: '🎯',
    duration: '2-4小时',
    activities: ['目标确认', '任务拆解', '工作量评估'],
    color: '#FFD700',
    angle: 0,
  },
  {
    name: '每日站会',
    nameEn: 'Daily Standup',
    icon: '🗣️',
    duration: '15分钟',
    activities: ['昨日进展', '今日计划', '阻碍排除'],
    color: '#4ECDC4',
    angle: 60,
  },
  {
    name: '冲刺开发',
    nameEn: 'Sprint Dev',
    icon: '💻',
    duration: '1-4周',
    activities: ['编码实现', '代码评审', '持续集成'],
    color: '#667EEA',
    angle: 120,
  },
  {
    name: '冲刺评审',
    nameEn: 'Sprint Review',
    icon: '🔍',
    duration: '1-2小时',
    activities: ['成果演示', '反馈收集', '需求调整'],
    color: '#9B59B6',
    angle: 180,
  },
  {
    name: '冲刺回顾',
    nameEn: 'Retrospective',
    icon: '🔄',
    duration: '1小时',
    activities: ['持续改进', '流程优化', '团队成长'],
    color: '#2ECC71',
    angle: 240,
  },
];

const VALUES = [
  { title: '个体和互动', sub: '高于流程和工具', icon: '🤝' },
  { title: '工作的软件', sub: '高于详尽的文档', icon: '⚡' },
  { title: '客户合作', sub: '高于合同谈判', icon: '🤲' },
  { title: '响应变化', sub: '高于遵循计划', icon: '🔄' },
];

export const AgileMethodAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleProg = spring({ frame, fps, config: { damping: 14, stiffness: 120 } });

  const cycleAngle = interpolate(frame, [80, 450], [0, 360], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const cx = 440;
  const cy = 550;
  const radius = 250;

  return (
    <AbsoluteFill
      style={{
        background: 'radial-gradient(ellipse at 30% 50%, #111827 0%, #0B0E17 100%)',
        fontFamily: 'system-ui, -apple-system, PingFang SC, sans-serif',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 36,
          left: 55,
          opacity: titleProg,
          transform: `translateY(${interpolate(titleProg, [0, 1], [-30, 0])}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 64,
            fontWeight: 800,
            margin: 0,
            background: 'linear-gradient(135deg, #4ECDC4, #667EEA)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          敏捷开发 Scrum
        </h1>
        <p style={{ fontSize: 28, margin: '6px 0 0', color: 'rgba(255,255,255,0.4)' }}>
          Agile Methodology · 迭代式软件开发
        </p>
      </div>

      {/* Sprint cycle center label */}
      <div
        style={{
          position: 'absolute',
          left: cx - 80,
          top: cy - 50,
          width: 160,
          height: 100,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ fontSize: 44, fontWeight: 800, color: '#FFD700' }}>Sprint</div>
        <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.4)' }}>迭代周期</div>
      </div>

      {/* Cycle ring */}
      <svg
        style={{ position: 'absolute', left: 0, top: 0, width: 900, height: 900 }}
      >
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={4}
        />
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke="#4ECDC4"
          strokeWidth={4}
          strokeDasharray={`${(cycleAngle / 360) * 2 * Math.PI * radius} ${2 * Math.PI * radius}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`}
          opacity={0.6}
        />
      </svg>

      {/* Phase nodes around the cycle */}
      {PHASES.map((phase, i) => {
        const angleRad = ((phase.angle - 90) * Math.PI) / 180;
        const nx = cx + Math.cos(angleRad) * radius;
        const ny = cy + Math.sin(angleRad) * radius;

        const nodeProg = spring({
          frame: frame - 30 - i * 14,
          fps,
          config: { damping: 12, stiffness: 140 },
        });

        return (
          <div
            key={phase.nameEn}
            style={{
              position: 'absolute',
              left: nx - 55,
              top: ny - 55,
              width: 110,
              height: 110,
              borderRadius: '50%',
              background: `${phase.color}18`,
              border: `3px solid ${phase.color}60`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: nodeProg,
              transform: `scale(${nodeProg})`,
              boxShadow: `0 4px 20px ${phase.color}25`,
            }}
          >
            <span style={{ fontSize: 34 }}>{phase.icon}</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: phase.color, marginTop: 2 }}>
              {phase.name}
            </span>
          </div>
        );
      })}

      {/* Right panel: phase details */}
      <div style={{ position: 'absolute', left: 900, top: 400, right: 50 }}>
        {PHASES.map((phase, i) => {
          const phaseStart = 40 + i * 70;
          const phaseEnd = phaseStart + 65;
          const fadeIn = interpolate(frame, [phaseStart, phaseStart + 15], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
          const fadeOut = interpolate(frame, [phaseEnd - 10, phaseEnd], [1, 0], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
          const opacity = i === PHASES.length - 1 ? fadeIn : fadeIn * fadeOut;

          if (frame < phaseStart || opacity <= 0) return null;

          return (
            <div
              key={phase.nameEn}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                opacity,
              }}
            >
              <div
                style={{
                  padding: '28px 32px',
                  background: `${phase.color}08`,
                  border: `2px solid ${phase.color}30`,
                  borderRadius: 20,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
                  <span style={{ fontSize: 40 }}>{phase.icon}</span>
                  <div>
                    <h3 style={{ fontSize: 34, fontWeight: 700, color: phase.color, margin: 0 }}>
                      {phase.name}
                    </h3>
                    <span style={{ fontSize: 28, color: 'rgba(255,255,255,0.35)' }}>
                      {phase.nameEn} · {phase.duration}
                    </span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 14 }}>
                  {phase.activities.map((act, ai) => (
                    <div
                      key={ai}
                      style={{
                        flex: 1,
                        padding: '14px 16px',
                        background: `${phase.color}12`,
                        borderRadius: 12,
                        border: `1px solid ${phase.color}25`,
                        fontSize: 28,
                        color: '#E5E7EB',
                        textAlign: 'center',
                      }}
                    >
                      {act}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom: Agile values */}
      <div
        style={{
          position: 'absolute',
          bottom: 46,
          left: 50,
          right: 50,
          display: 'flex',
          gap: 14,
        }}
      >
        {VALUES.map((v, i) => {
          const vProg = spring({
            frame: frame - 420 - i * 10,
            fps,
            config: { damping: 14, stiffness: 120 },
          });

          return (
            <div
              key={v.title}
              style={{
                flex: 1,
                padding: '12px 16px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 12,
                opacity: vProg,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <span style={{ fontSize: 36 }}>{v.icon}</span>
              <div>
                <div style={{ fontSize: 28, fontWeight: 600, color: '#4ECDC4' }}>{v.title}</div>
                <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.35)' }}>{v.sub}</div>
              </div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};
